
import { action, observable, computed } from 'mobx';
import { isString, isObject, isArray, isBoolean, isNumber } from '~/common/utils/typeCheckers';
import { convertNumberFormat } from '~/common/utils/numberOperations.js';
import isPlainObject from 'lodash/isPlainObject';
import * as Sentry from '@sentry/react';

import authUser from '~/common/services/authUser';
import getLmiUser from '~/common/services/lmiUser';
import userInfo from '~/common/services/userInfo';

import { formsStore } from '~/common/stores/ui/forms';
import deepGet from '~/common/utils/deepGet';
import { gtm, loginEvent } from '~/common/utils/tracking';

export const FLOWS_ENUM = {
    ANNUAL_UPGRADE: 'annual-upgrade',
    RUBY: 'ruby',
    TRIAL_UPGRADE: 'trial-upgrade'
};

class LoginClass {
    @observable authToken = '';
    @observable lmiAuthcode = '';
    @observable userInfo = {};
    @observable loginByUsername = false;
    @observable logInFlow = '';
    @observable loginError = '';
    @observable userNumbers = [];
    @observable trialNumberInfo = {};
    @observable isFetchingAuthUser = false;
    @observable isFetchingLmiUser = false;
    @observable isFetchingUserInfo = false;
    @observable requestId = '';
    @observable showVerification = false;
    @observable maskedEmail = '';
    @observable resendInterval = 0;
    @observable authError = '';
    @observable authLoading = false;

    @action setAuthToken(str) {
        this.authToken = isString(str, this.authToken);
    }

    @action setLmiAuthcode(str) {
        this.lmiAuthcode = isString(str, this.lmiAuthcode);
    }

    @action setUserInfo(obj) {
        this.userInfo = isObject(obj, this.userInfo);
    }

    @action setLoginError(str) {
        this.loginError = isString(str, this.loginError);
    }

    @action setLogInFlow(str) {
        if (Object.values(FLOWS_ENUM).includes(str)) {
            this.logInFlow = isString(str, this.logInFlow);
        } else {
            const flowEnumValues = Object.values(FLOWS_ENUM).join(',');
            throw new Error(`setLogInFlow() accept only values from FLOWS_ENUM : ${flowEnumValues}`);
        }
    }

    @action setUserNumbers(arr) {
        this.userNumbers = isArray(arr, this.userNumbers);
    }

    @action setTrialNumberInfo(obj) {
        this.trialNumberInfo = isObject(obj, this.trialNumberInfo);
    }

    @action setLoginByUsername(bool) {
        this.loginByUsername = isBoolean(bool, this.loginByUsername);
    }

    @action setIsFetchingAuthUser(bool) {
        this.isFetchingAuthUser = isBoolean(bool, this.isFetchingAuthUser);
    }

    @action setIsFetchingLmiUser(bool) {
        this.isFetchingLmiUser = isBoolean(bool, this.isFetchingLmiUser);
    }

    @action setIsFetchingUserInfo(bool) {
        this.isFetchingUserInfo = isBoolean(bool, this.isFetchingUserInfo);
    }

    @action setShowVerification(bool) {
        this.showVerification = isBoolean(bool, this.showVerification);
    }

    @action setMaskedEmail(str) {
        this.maskedEmail = isString(str, this.maskedEmail);
    }

    @action setResendInterval(num) {
        this.resendInterval = isNumber(num, this.resendInterval);
    }

    @action setRequestId(str) {
        this.requestId = isString(str, this.requestId);
    }

    @action setAuthError(str) {
        this.authError = isString(str, this.authError);
    }

    @action setAuthVerificationLoading(bool) {
        this.authLoading = isBoolean(bool, this.authLoading);
    }

    @action clearLogin() {
        this.setUserInfo({});
        this.setAuthToken('');
        this.setLmiAuthcode('');
    }

    @action authUser(errorCallback) {
        const { Username, Email, PasswordLogin } = formsStore;

        // clear any possible errors
        this.setLoginError('');
        formsStore.clearFieldError('Username');
        formsStore.clearFieldError('Email');
        formsStore.clearFieldError('PasswordLogin');

        // pass either username or email depending on log in mode
        const userIdentification = this.loginByUsername ? Username : Email;
        this.getVerificationCode({ userIdentification, PasswordLogin }, errorCallback);
        this.setIsFetchingAuthUser(true);
    }

    @action getVerificationCode(data, errorCallback) {
        const { userIdentification, PasswordLogin } = data;

        authUser({ username: userIdentification, password: PasswordLogin, lmiAuthcode: this.lmiAuthcode }).then((response) => {
            const requestId = deepGet(response, ['data', 'RequestId']);
            const requestInterval = deepGet(response, ['data', 'RequestInterval']);
            const maskedEmail = deepGet(response, ['data', 'MaskedEmail']);
            this.setMaskedEmail(maskedEmail);
            this.setResendInterval(requestInterval);
            this.setRequestId(requestId);
            if (requestId) {
                this.setShowVerification(true);
            }
            this.setIsFetchingAuthUser(false);
        }).catch((err) => {
            if (errorCallback) {
                errorCallback(err);
            } else {
                const errorResponseData = deepGet(err, ['response', 'data']);
                const errorString = deepGet(errorResponseData, ['Errors', 0]);
                if (typeof errorString === 'string') {
                    const errorWithCode = errorString.includes('Error') && errorString.split(':');
                    if (errorWithCode) {
                        this.setLoginError(errorWithCode[1].trim());
                    } else {
                        // error was from invalid user credentials, alert user
                        this.setLoginError(errorString);

                        // this.setLoginError('Your email address and/or password are incorrect.');
                    }
                } else {
                    // report a general error and alert user
                    this.setLoginError('Something went wrong. Please try again.');
                }
                Sentry.captureMessage('userLoginError', { extra: {
                    errorObj: errorResponseData
                } });
            }
            this.setIsFetchingAuthUser(false);
        });
    }

    @action getNewVerificationCode() {
        this.setAuthVerificationLoading(true);
        this.setAuthError('');
        this.setResendInterval(0);

        authUser({ lastRequestId: this.requestId }).then((response) => {
            const newRequestId = deepGet(response, ['data', 'RequestId']);
            const requestInterval = deepGet(response, ['data', 'RequestInterval']);
            const maskedEmail = deepGet(response, ['data', 'MaskedEmail']);
            this.setMaskedEmail(maskedEmail);
            this.setResendInterval(requestInterval);
            this.setRequestId(newRequestId);
            this.setAuthVerificationLoading(false);
        }).catch((err) => {
            this.setAuthError('');
            this.setAuthVerificationLoading(false);
            const errorResponseData = deepGet(err, ['response', 'data']);
            const errorString = deepGet(errorResponseData, ['Errors', 0]);
            const errorWithCode = errorString.includes('Error') && errorString.split(':');
            if (errorWithCode) {
                if (errorWithCode[0] === 'Error 1010' || errorWithCode[0] === 'Error 1006') {
                    this.setShowVerification(false);
                    this.setResendInterval(0);
                    this.setLoginError(errorWithCode[1].trim());
                } else {
                    this.setAuthError(errorWithCode[1].trim());
                }
            } else {
                this.setAuthError('Verification failed. Try again or resend a new code.');
            }
            Sentry.captureMessage('resendAuthVerificationCodeError', { extra: {
                errorObj: errorResponseData
            } });
        });
    }

    @action validateUser(code, callBack, errorCallback, fetchUserInfo) {
        const requestID = this.requestId;
        this.setAuthVerificationLoading(true);
        authUser({ requestID, code }).then((response) => {
            // set token
            const authToken = deepGet(response, ['data', 'Token']);
            this.setAuthToken(authToken);
            this.setAuthVerificationLoading(false);
            this.setShowVerification(false);
            if (callBack && !fetchUserInfo) callBack(authToken);
            if (fetchUserInfo) {
                this.fetchUserInformations(callBack, true);
            }
        }).catch((err) => {
            if (errorCallback) {
                errorCallback(err);
            } else {
                const errorResponseData = deepGet(err, ['response', 'data']);
                const errorString = deepGet(errorResponseData, ['Errors', 0]);
                if (typeof errorString === 'string') {
                    const errorWithCode = errorString.includes('Error') && errorString.split(':');
                    if (errorWithCode) {
                        if (errorWithCode[0] === 'Error 1010' || errorWithCode[0] === 'Error 1006') {
                            this.setShowVerification(false);
                            this.setResendInterval(0);
                            this.setLoginError(errorWithCode[1].trim());
                        } else {
                            this.setAuthError(errorWithCode[1].trim());
                        }
                    }
                } else {
                    // report a general error and alert user
                    this.setAuthError('Verification failed. Try again or resend a new code.');
                }
                Sentry.captureMessage('userValidationCodeError', { extra: {
                    errorObj: errorResponseData
                } });
            }
            this.setAuthVerificationLoading(false);
        });
    }

    @action fetchUserInformations = (callBack, useAuthTokenInCallback) => {
        if (this.authToken) {
            this.setIsFetchingUserInfo(true);
            userInfo(this.authToken).then((userInfoResponse) => {
                this.setUserInfo(userInfoResponse.data);
                this.setIsFetchingUserInfo(false);
                gtm(loginEvent());

                if (callBack) callBack(useAuthTokenInCallback ? this.authToken : userInfoResponse);
            }).catch(() => {
                this.setLoginError('There was a problem loading your user information.');
                this.setIsFetchingUserInfo(false);
            });
        } else {
            this.setLoginError('There was a problem loading your user information.');
        }
    };

    @action loadLmiUserInfo = (callBack) => {
        if (this.lmiAuthcode) {
            this.setIsFetchingLmiUser(true);
            getLmiUser(this.lmiAuthcode).then((response) => {
                const { data } = response;
                // Update the form with the retrieved user's information
                if (data && data.Email) formsStore.setFormData('Email', data.Email);
                if (data && data.FirstName) formsStore.setFormData('FirstName', data.FirstName);
                if (data && data.LastName) formsStore.setFormData('LastName', data.LastName);
                if (data && data.UserKey) formsStore.setFormData('UserKey', data.UserKey);
                this.setIsFetchingLmiUser(false);
                if (callBack) callBack(data);
            }).catch(() => {
                this.setLoginError('Something went wrong. Please try again.');
                this.setIsFetchingLmiUser(false);
            });
        } else {
            this.setLoginError('Something went wrong. Please try again.');
        }
    };

    @computed get userFirstNumber() {
        // const numbersArray = this.userNumbers;
        const number = this.userNumbers.find((num, i) => {
            return i === 0;
        });

        if (!number) return false;
        return convertNumberFormat(number.Number, 'UI');
    }

    @computed get userNumber() {
        if (isPlainObject(this.userInfo) && this.userInfo.CancelledNumbers && this.userInfo.CancelledNumbers[0]) {
            return this.userInfo.CancelledNumbers;
        } else if (isPlainObject(this.trialNumberInfo) && this.trialNumberInfo.Number) {
            return this.trialNumberInfo.Number;
        }
        return false;
    }

    @computed get getLogInFlow() {
        return this.logInFlow;
    }

    @computed get isRubyLogInFlow() {
        return this.logInFlow && this.logInFlow === FLOWS_ENUM.RUBY;
    }

    @computed get isAnnualUpgradeLogInFlow() {
        return this.logInFlow && this.logInFlow === FLOWS_ENUM.ANNUAL_UPGRADE;
    }

    @computed get isTrialUpgradeLogInFlow() {
        return this.logInFlow && this.logInFlow === FLOWS_ENUM.TRIAL_UPGRADE;
    }

    @computed get userOfferSuiteID() {
        return isPlainObject(this.userInfo) && this.userInfo.userOfferSuiteID;
    }

    @computed get userOfferId() {
        return isPlainObject(this.userInfo) && this.userInfo.OfferID;
    }

    @computed get userOfferSuiteId() {
        return isPlainObject(this.userInfo) && this.userInfo.OfferSuiteID;
    }

    @computed get userVpsId() {
        return isPlainObject(this.userInfo) && this.userInfo.VpsId;
    }

    @computed get userCount() {
        return isPlainObject(this.userInfo) && this.userInfo.UserCount;
    }

    @computed get isLoading() {
        return this.isFetchingAuthUser || this.isFetchingLmiUser || this.isFetchingUserInfo;
    }
}

const loginStore = new LoginClass();
export { loginStore, LoginClass };
